import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Package, Truck, CheckSquare, Printer, Tag } from 'lucide-react';
import api from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Card from '@/components/ui/Card';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

interface Order {
  id: string;
  order_number: string;
  email: string;
  status: string;
  total: number;
  item_count: number;
  created_at: string;
  first_name: string;
  last_name: string;
}

interface Shipment {
  id: string;
  order_id: string;
  order_number: string;
  email: string;
  carrier: string;
  service_name: string;
  tracking_number: string;
  tracking_url: string;
  label_cost: number;
  status: string;
  created_at: string;
}

const SHIPMENT_STATUS_BADGES: Record<string, 'yellow' | 'blue' | 'green' | 'red' | 'gray'> = {
  label_created: 'yellow',
  picked_up: 'blue',
  in_transit: 'blue',
  delivered: 'green',
  returned: 'red',
  cancelled: 'gray',
};

export default function Fulfillment() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'queue';

  const [orders, setOrders] = useState<Order[]>([]);
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrders, setSelectedOrders] = useState<Set<string>>(new Set());
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchCarrier, setBatchCarrier] = useState('usps');
  const [batchService, setBatchService] = useState('usps_1');
  const [batchWeight, setBatchWeight] = useState('8');

  useEffect(() => {
    if (tab === 'queue') fetchQueue();
    else fetchShipments();
  }, [tab]);

  async function fetchQueue() {
    setIsLoading(true);
    try {
      const res = await api.get<{ orders: Order[] }>('/admin/orders?status=processing&limit=100');
      setOrders(res.orders);
    } catch { /* ignore */ } finally { setIsLoading(false); }
  }

  async function fetchShipments() {
    setIsLoading(true);
    try {
      const res = await api.get<{ shipments: Shipment[] }>('/admin/fulfillment/shipments?limit=100');
      setShipments(res.shipments);
    } catch { /* ignore */ } finally { setIsLoading(false); }
  }

  function toggleSelect(id: string) {
    setSelectedOrders(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selectedOrders.size === orders.length) setSelectedOrders(new Set());
    else setSelectedOrders(new Set(orders.map(o => o.id)));
  }

  async function handleBatchLabels() {
    if (selectedOrders.size === 0) return;
    setIsBatchProcessing(true);
    try {
      await api.post('/admin/fulfillment/batch/labels', {
        order_ids: Array.from(selectedOrders),
        carrier: batchCarrier,
        service_code: batchService,
        weight_oz: parseFloat(batchWeight),
      });
      setShowBatchModal(false);
      setSelectedOrders(new Set());
      fetchQueue();
    } catch (err: any) {
      alert(err.message || 'Batch label creation failed');
    } finally { setIsBatchProcessing(false); }
  }

  async function handleBatchShip() {
    if (selectedOrders.size === 0) return;
    setIsBatchProcessing(true);
    try {
      await api.post('/admin/fulfillment/batch/ship', { order_ids: Array.from(selectedOrders) });
      setSelectedOrders(new Set());
      fetchQueue();
    } catch { /* ignore */ } finally { setIsBatchProcessing(false); }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Fulfillment</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {[
          { key: 'queue', label: 'Fulfillment Queue', icon: Package },
          { key: 'shipments', label: 'Shipment History', icon: Truck },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setSearchParams({ tab: t.key })}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key ? 'border-green-600 text-green-700' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <t.icon className="h-4 w-4" />
            {t.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12"><LoadingSpinner /></div>
      ) : tab === 'queue' ? (
        <>
          {/* Bulk Actions */}
          {selectedOrders.size > 0 && (
            <Card>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm text-gray-600">{selectedOrders.size} selected</span>
                <Button size="sm" variant="primary" onClick={() => setShowBatchModal(true)}>
                  <Tag className="h-4 w-4 mr-1" />Create Labels
                </Button>
                <Button size="sm" variant="outline" onClick={handleBatchShip} isLoading={isBatchProcessing}>
                  <Truck className="h-4 w-4 mr-1" />Mark Shipped
                </Button>
              </div>
            </Card>
          )}

          {orders.length === 0 ? (
            <Card padding="lg">
              <div className="text-center py-8">
                <CheckSquare className="h-12 w-12 text-green-300 mx-auto mb-3" />
                <p className="text-gray-500">All caught up! No orders waiting for fulfillment.</p>
              </div>
            </Card>
          ) : (
            <Card padding="none">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input type="checkbox" checked={selectedOrders.size === orders.length && orders.length > 0}
                          onChange={toggleAll} className="rounded border-gray-300" />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Order</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Customer</th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Date</th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500">Items</th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500">Total</th>
                      <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {orders.map(order => (
                      <tr key={order.id} className={selectedOrders.has(order.id) ? 'bg-green-50' : ''}>
                        <td className="px-4 py-3">
                          <input type="checkbox" checked={selectedOrders.has(order.id)}
                            onChange={() => toggleSelect(order.id)} className="rounded border-gray-300" />
                        </td>
                        <td className="px-4 py-3">
                          <Link to={`/admin/orders/${order.id}`} className="text-sm font-medium text-blue-600 hover:underline">
                            {order.order_number}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {order.first_name} {order.last_name}
                          <div className="text-xs text-gray-500">{order.email}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">{formatDate(order.created_at)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right">{order.item_count}</td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">{formatCurrency(order.total)}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex gap-1 justify-end">
                            <Link to={`/admin/orders/${order.id}`}>
                              <Button size="sm" variant="outline">Ship</Button>
                            </Link>
                            <a href={`${import.meta.env.VITE_API_URL || ''}/api/admin/fulfillment/packing-slip/${order.id}`}
                              target="_blank" rel="noopener noreferrer">
                              <Button size="sm" variant="ghost"><Printer className="h-4 w-4" /></Button>
                            </a>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      ) : (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Order</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Carrier</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Tracking</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500">Cost</th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {shipments.map(s => (
                  <tr key={s.id}>
                    <td className="px-4 py-3">
                      <Link to={`/admin/orders/${s.order_id}`} className="text-sm text-blue-600 hover:underline">
                        {s.order_number}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 uppercase">{s.carrier}</td>
                    <td className="px-4 py-3 text-sm">
                      {s.tracking_url ? (
                        <a href={s.tracking_url} target="_blank" rel="noopener" className="text-blue-600 hover:underline font-mono text-xs">{s.tracking_number}</a>
                      ) : <span className="font-mono text-xs text-gray-500">{s.tracking_number}</span>}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={SHIPMENT_STATUS_BADGES[s.status] || 'gray'}>
                        {s.status.replace(/_/g, ' ')}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{formatCurrency(s.label_cost)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{formatDate(s.created_at)}</td>
                  </tr>
                ))}
                {shipments.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-gray-400">No shipments yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Batch Label Modal */}
      <Modal isOpen={showBatchModal} onClose={() => setShowBatchModal(false)} title="Batch Create Labels">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">Create shipping labels for {selectedOrders.size} selected orders.</p>
          <Select label="Carrier" value={batchCarrier} onChange={e => setBatchCarrier(e.target.value)}
            options={[{ label: 'USPS', value: 'usps' }, { label: 'FedEx', value: 'fedex' }]} />
          <Select label="Service" value={batchService} onChange={e => setBatchService(e.target.value)}
            options={
              batchCarrier === 'usps'
                ? [{ label: 'Priority Mail', value: 'usps_1' }, { label: 'First Class', value: 'usps_0' }, { label: 'Express', value: 'usps_3' }]
                : [{ label: 'FedEx Ground', value: 'fedex_FEDEX_GROUND' }, { label: 'Express Saver', value: 'fedex_FEDEX_EXPRESS_SAVER' }]
            } />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Weight (oz)</label>
            <input type="number" value={batchWeight} onChange={e => setBatchWeight(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowBatchModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleBatchLabels} isLoading={isBatchProcessing}>
              Create {selectedOrders.size} Labels
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
